import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight } from 'lucide-react';
import { useCursorContext } from '../../hooks/useCursorContext';
import { nikoInfo, stats } from '../../data/portfolioData';
import { AnimatedCounter } from '../ui/AnimatedCounter';

gsap.registerPlugin(ScrollTrigger);

export function AboutTeaser() {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const { setCursorState } = useCursorContext();

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Text reveal
      const lines = textRef.current?.querySelectorAll('.reveal-line');
      if (lines) {
        lines.forEach((line) => {
          gsap.fromTo(
            line,
            { y: 60, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.8,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: line,
                start: 'top 85%',
              },
            }
          );
        });
      }

      // Stats animation
      const statItems = statsRef.current?.querySelectorAll('.stat-item');
      if (statItems) {
        gsap.fromTo(
          statItems,
          { y: 30, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.6,
            stagger: 0.1,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: statsRef.current,
              start: 'top 80%',
            },
          }
        );
      }
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={containerRef} className="relative py-32 md:py-48 px-8 md:px-12">
      <div className="max-w-6xl mx-auto">
        {/* Section label */}
        <div className="mb-16 flex items-center justify-between">
          <span className="text-xs text-white/30 tracking-[0.3em] uppercase">
            Über mich
          </span>
          <Link
            to="/about"
            className="text-sm text-emerald-500 hover:text-emerald-400 transition-colors flex items-center gap-2 group"
            onMouseEnter={() => setCursorState('hovering')}
            onMouseLeave={() => setCursorState('default')}
          >
            Mehr erfahren
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Main text */}
        <div ref={textRef} className="space-y-6 mb-16">
          <p className="reveal-line text-3xl md:text-5xl lg:text-6xl font-light text-white/90 leading-tight">
            {nikoInfo.shortBio}
          </p>
          <p className="reveal-line text-xl md:text-2xl font-light text-white/50 leading-relaxed max-w-4xl">
            {nikoInfo.fullBio.split('.').slice(0, 2).join('.')}.
          </p>
        </div>

        {/* Stats */}
        <div ref={statsRef} className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, i) => (
            <div
              key={i}
              className="stat-item p-6 border border-white/5 rounded-xl hover:border-emerald-500/30 transition-colors"
              onMouseEnter={() => setCursorState('hovering')}
              onMouseLeave={() => setCursorState('default')}
            >
              <AnimatedCounter
                value={stat.value}
                className="text-3xl md:text-4xl font-light text-emerald-500"
              />
              <span className="block text-sm text-white/40 mt-2">{stat.label}</span>
            </div>
          ))}
        </div>

        {/* CTA Link */}
        <div className="mt-12 reveal-line">
          <Link
            to="/about"
            className="inline-flex items-center gap-4 px-8 py-4 border border-white/10 rounded-full group hover:border-emerald-500/50 transition-colors"
            onMouseEnter={() => setCursorState('hovering')}
            onMouseLeave={() => setCursorState('default')}
          >
            <span className="text-white/60 group-hover:text-white transition-colors">
              Skills, Branchen & kompletter Werdegang
            </span>
            <ArrowRight className="w-5 h-5 text-emerald-500 group-hover:translate-x-2 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
}
